import { userLogout } from "@/actions/user.actions";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import {
  CookiesEnum,
  cookiesValues,
  EventNamesEnum,
  EventPropertiesEnum,
  SessionStorageKeysEnum,
  SourcePagesEnum,
} from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useTrackingAnalytics } from "@/hooks/useTrackingAnalytics";
import usePreventAutoFocus from "@/hooks/usePreventAutofocus";
import { AuthSwitch } from "@/screens/settings/components/AuthSwitch";
import { PolicyAboutUs } from "@/screens/settings/components/PolicyAboutUs";
import {
  getClientSideCookieValue,
  getPaywallLink,
  isTvEnv,
  shouldRedirectToSubscriptionFlow,
} from "@/utils/common.helpers";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { Box } from "../Box";
import { Divider } from "../Divider";
import { EditIcon, PlusIcon } from "../Icons";
import { Img } from "../Img";
import { Column, Row } from "../Stack";
import { SubscriptionDetails } from "../SubscriptionDetails";
import { Text } from "../Text";
import { ContentCultureSelection } from "./ContentCultureSelection";
import { Button } from "../Button";
import { MAX_PROFILES } from "@/constants/constants";
import { useDialect } from "@/hooks/useDialect";
interface SettingsComponentProps {
  onClose: () => void;
}

export const SettingsDrawer = ({ onClose }: SettingsComponentProps) => {
  const preventAutoFocus = usePreventAutoFocus();
  const router = useRouter();

  const { isLoggedIn, isTrialEligibleUser, logout } = useUserContext();
  const { currentDialect } = useDialect();
  const { t, currentLanguage } = useClientTranslations();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

  // Check if dialect cookie exists
  const dialectCookie = getClientSideCookieValue(
    cookiesValues[CookiesEnum.Dialect],
  );
  const hasDialectCookie = !!dialectCookie;

  const handleLogout = async () => {
    await userLogout();
    logout();
    if (isTvEnv()) {
      router.replace(`/${currentLanguage}/login`);
    } else {
      router.replace("/logout?redirectAfterLogoutTo=/");
    }
  };
  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent
        className="!max-w-[422px] bg-black !p-0"
        {...preventAutoFocus}
      >
        <div className="flex h-full w-full flex-col overflow-y-auto rounded-tl-[0.5rem] border-2 border-stg-grey-1 pt-28">
          {showLogoutConfirmation ? (
            <LogoutConfirmation
              onConfirm={handleLogout}
              onCancel={() => setShowLogoutConfirmation(false)}
            />
          ) : (
            <Box fillInFlex>
              <Column gap={2}>
                {!isLoggedIn ? (
                  <LogoutContent />
                ) : (
                  <div className="relative w-full rounded-t-[2.5rem] bg-stg-grey-1 p-6">
                    <ProfileSection />
                    <Divider />
                    <SubscriptionDetails />
                  </div>
                )}

                {/* Content Culture Selection */}
                {hasDialectCookie && (
                  <Box paddingHorizontal={6} width="w-full">
                    <ContentCultureSelection
                      dialect={currentDialect.urlValue}
                    />
                  </Box>
                )}

                <Divider
                  height="large"
                  hasMargin={false}
                  bgColorType="settings"
                />
                {/* Legal Section */}
                <Box paddingHorizontal={6} width="w-full">
                  <PolicyAboutUs isExpanded={false} />
                </Box>
              </Column>
              <div className=" w-full px-4 pt-4">
                {isLoggedIn && (
                  <AuthSwitch
                    openPopup={() => {
                      setShowLogoutConfirmation(true);
                    }}
                  />
                )}
              </div>
            </Box>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

const ProfileSection = ({}) => {
  const { selectedProfile, profiles, setSelectedProfile, user } =
    useUserContext();
  const { t, currentLanguage } = useClientTranslations();
  const { trackEvent } = useTrackingAnalytics();
  const canAddMoreProfiles = profiles.length < MAX_PROFILES;

  if (!selectedProfile) {
    return null;
  }
  const nonSelectedProfiles = profiles.filter(
    (profile) => profile.profileId !== selectedProfile?.profileId,
  );
  const isSingleProfile = nonSelectedProfiles.length < 1;
  const handleProfileActionClick = (isEditFlow?: boolean) => {
    if (isEditFlow) {
      sessionStorage.setItem(
        SessionStorageKeysEnum.EDIT_PROFILE_ID,
        selectedProfile.profileId.toString(),
      );
    }

    if (user?.userId) {
      trackEvent({
        eventName: isEditFlow
          ? EventNamesEnum.EDIT_PROFILE_CTA_CLICKED
          : EventNamesEnum.ADD_PROFILE_CTA_CLICKED,
        properties: {
          [EventPropertiesEnum.ACTION_SOURCE]: SourcePagesEnum.SETTINGS,
        },
      });
    }
  };
  const handleProfileSelectionClick = (
    profileId: string,
    displayName: string,
  ) => {
    setSelectedProfile(profileId);
    if (user?.userId) {
      trackEvent({
        eventName: EventNamesEnum.PROFILE_CARD_SELECTED,
        properties: {
          [EventPropertiesEnum.ACTION_SOURCE]: SourcePagesEnum.SETTINGS,
          [EventPropertiesEnum.TARGET_PROFILE_ID]: profileId,
          [EventPropertiesEnum.TARGET_PROFILE_NAME]: displayName,
        },
      });
    }
  };
  return (
    <>
      <div className="absolute left-1/2 top-0 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center gap-3">
        <Link
          href={`/${currentLanguage}/profile/edit`}
          onClick={() => {
            const isEditFlow = true;
            handleProfileActionClick(isEditFlow);
          }}
          className="group relative cursor-pointer"
        >
          <Img
            src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${selectedProfile.avatar}`}
            alt={selectedProfile.displayName}
            className="rounded-lg border-2 border-white transition-all duration-200 "
            width={100}
            height={100}
          />
          <div className="absolute inset-0 rounded-lg bg-black/0 transition-all duration-200 group-hover:border-2 group-hover:border-white group-hover:bg-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="flex items-center gap-1.5 rounded-full bg-black/60 px-2 py-1 group-hover:border-2 group-hover:border-white ">
              <EditIcon width={14} height={14} />
              <Text size={2} weight="medium" appearance="primary">
                {t("common.edit")}
              </Text>
            </div>
          </div>
        </Link>
      </div>
      <Box paddingTop={8} width="w-full">
        <Column
          gap={2}
          align="center"
          justify="center"
          fullWidth={true}
          useGrid
        >
          <Text size={5} weight="bold" alignment="center">
            {selectedProfile.displayName}
          </Text>

          <Row gap={2} align="center" fullWidth={true} justify="center">
            {nonSelectedProfiles.map((profile) => (
              <button
                key={profile.profileId}
                onClick={() => {
                  handleProfileSelectionClick(
                    profile.profileId,
                    profile.displayName,
                  );
                }}
              >
                <Column
                  gap={0.5}
                  justify="center"
                  fullWidth={true}
                  useGrid
                  align="center"
                >
                  <Img
                    src={`${process.env.NEXT_PUBLIC_CDN_ROOT_URL}${profile.avatar}`}
                    alt={profile.displayName}
                    className="mx-auto rounded-lg bg-stg-grey-3 transition-transform duration-200 hover:scale-110"
                    width={60}
                    height={60}
                  />
                  <div className="max-w-[3.75rem] truncate text-xs font-medium text-stg-grey-4">
                    {profile.displayName}
                  </div>
                </Column>
              </button>
            ))}
            {canAddMoreProfiles && (
              <Link
                href={`/${currentLanguage}/profile/create`}
                onClick={() => {
                  handleProfileActionClick();
                }}
              >
                <Column gap={0.5} justify="center" fullWidth={true} useGrid>
                  <div
                    className={`flex items-center justify-center rounded-lg bg-stg-grey-3 transition-transform duration-200 hover:scale-110 ${
                      isSingleProfile
                        ? "h-10 gap-2 rounded-full p-2 "
                        : "h-[3.75rem] w-[3.75rem]"
                    }`}
                  >
                    <PlusIcon
                      width={isSingleProfile ? 16 : 24}
                      height={isSingleProfile ? 16 : 24}
                    />
                    {isSingleProfile && (
                      <Text
                        size={2}
                        weight="medium"
                        alignment="center"
                        appearance="primary"
                      >
                        {t("profile_assets.add_profile")}
                      </Text>
                    )}
                  </div>
                  {!isSingleProfile && (
                    <Text
                      size={2}
                      weight="medium"
                      alignment="center"
                      appearance="secondary"
                    >
                      {t("profile_assets.add_profile")}
                    </Text>
                  )}
                </Column>
              </Link>
            )}
          </Row>
        </Column>
      </Box>
    </>
  );
};

export const StartTrialSection = ({
  isTrialEligibleUser,
  currentLanguage,
  dialect,
}: {
  isTrialEligibleUser: boolean;
  currentLanguage: string;
  dialect: string;
}) => {
  const { t } = useClientTranslations();
  return (
    <>
      <Link
        href={getPaywallLink(
          isTrialEligibleUser,
          shouldRedirectToSubscriptionFlow(),
          currentLanguage,
          dialect,
        )}
        className="flex h-10 w-full items-center  justify-center  rounded-lg bg-white  !text-black hover:opacity-90"
        data-testid="start-trial-button"
      >
        <Text appearance="black" weight="bold">
          {isTrialEligibleUser && !shouldRedirectToSubscriptionFlow()
            ? t("home_page.header_right.start_trial")
            : t("home_page.header_right.purchase_subscription")}
        </Text>
      </Link>
    </>
  );
};

const LogoutConfirmation = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const { t } = useClientTranslations();

  return (
    <div className="flex h-full w-full items-center justify-center p-4">
      <Column gap={4} align="center" justify="center" fullWidth={true}>
        <Text size={5} weight="bold" alignment="center">
          {t("popups.logout.heading")}
        </Text>
        <Row
          wrap={false}
          gap={3}
          align="center"
          justify="center"
          fullWidth={true}
        >
          <Button
            variant="outlined-white"
            onClick={onConfirm}
            className="group transition-colors hover:!border-stg-error hover:!bg-stg-error [&>div>div]:group-hover:!text-white"
          >
            <Text weight="bold">{t("popups.logout.confirmCta")}</Text>
          </Button>
          <Button
            variant="outlined-white"
            onClick={onCancel}
            className="group transition-colors hover:!border-white hover:!bg-white group-hover:[&_*]:!text-black"
          >
            <Text
              weight="bold"
              dangerouslyDisableDefaultTextColor={true}
              classNames="text-white group-hover:!text-black"
            >
              {t("popups.logout.cancelCta")}
            </Text>
          </Button>
        </Row>
      </Column>
    </div>
  );
};

const LogoutContent = () => {
  const { t } = useClientTranslations();
  const { isTrialEligibleUser } = useUserContext();
  const { currentLanguage } = useClientTranslations();
  const { currentDialect } = useDialect();
  return (
    <>
      <Box paddingHorizontal={6} width="w-full">
        <Box paddingHorizontal={6} paddingBottom={4}>
          <Column gap={1} align="center">
            <span className="text-[1.75rem] font-bold">
              {t("setting_page.welcomeMessage")}
            </span>
            <Text
              appearance="secondary"
              size={2}
              weight="medium"
              alignment="center"
            >
              {t("setting_page.welcomeMessageDescription")}
            </Text>
          </Column>
        </Box>

        <Column gap={1} fullWidth={true}>
          <Column gap={1} fullWidth={true}>
            <AuthSwitch openPopup={() => {}} />
            <StartTrialSection
              isTrialEligibleUser={isTrialEligibleUser}
              currentLanguage={currentLanguage}
              dialect={currentDialect.urlValue}
            />
          </Column>
        </Column>
      </Box>

      <Divider hasMargin={false} height="large" />
    </>
  );
};
